@if (inputFieldConfig) {
  <div [formGroup]="formGroup" class="form-group">
    <div class="main-wrapper pointer-events-none">
      @let parentControl = $any(formGroup.get(inputFieldConfig.name)) ?? undefined;
      @switch (inputFieldConfig.inputFormat) {
        <!-- Short Text Input -->
        @case (InputMode.SHORT_TEXT_INPUT) {
          <ng-container
            [ngTemplateOutlet]="inputTextTemplate"
            [ngTemplateOutletContext]="{
              config: inputFieldConfig,
              group: formGroup,
            }"
          >
          </ng-container>
        }

        <!-- Email Input -->
        @case (InputMode.EMAIL) {
          <finxone-email
            [id]="inputFieldConfig.name"
            [formGroup]="formGroup"
            [formControlName]="inputFieldConfig.name"
            [parentControl]="parentControl"
            [label]="inputFieldConfig?.label ?? ''"
            [type]="inputFieldConfig?.type ?? 'email'"
            [placeholder]="inputFieldConfig?.inputPlaceholder ?? ''"
            [validationConfig]="validationConfig"
            [infoText]="renderedHelperTextContent"
            [isVerifyButtonShown]="inputFieldConfig?.showEmailVerifyButton ?? false"
            [isVerifyButtonGlobalStylingAllowed]="isVerifyButtonGlobalStylingAllowed"
            [verifyButtonGlobalStylingClass]="verifyButtonGlobalStylingClass"
            [verifyButtonStyle]="verifyButtonStyle"
            [verifyButtonTextContent]="verifyButtonTextContent"
            [spaceBetweenElementUnit]="inputFieldConfig?.spaceBetweenElementUnit ?? ''"
            [spaceBetweenElementValue]="inputFieldConfig?.spaceBetweenElementValue ?? 0"
            [disableAutocomplete]="disableAutocomplete"
            [navigationOptions]="widgetNavigationOptions"
            [inputFormat]="inputFieldConfig.inputFormat"
            [parentFormId]="inputFieldConfig?.parentForm ?? ''"
            [nestedFormId]="nestedFormId"
            [inputLabelTooltipText]="renderedLabelTooltipTextContent"
          ></finxone-email>
        }

        <!-- Number Input -->
        @case (InputMode.NUMBER) {
          <ng-container
            [ngTemplateOutlet]="inputTextTemplate"
            [ngTemplateOutletContext]="{
              config: inputFieldConfig,
              group: formGroup,
            }"
          >
          </ng-container>
        }

        <!-- Phone Number Input -->
        @case (InputMode.PHONE_NUMBER) {
          <finxone-phone-number
            [id]="inputFieldConfig.name"
            [name]="inputFieldConfig.name"
            [label]="inputFieldConfig?.label ?? ''"
            [placeholder]="inputFieldConfig?.inputPlaceholder ?? ''"
            [formControlName]="inputFieldConfig.name"
            [parentControl]="parentControl"
            [value]="formGroup?.get(inputFieldConfig.name)?.value"
            [countryCodeFormControl]="getFormControlByName(inputFieldConfig.countryCode || 'countryCode')"
            [countryCode]="inputFieldConfig?.countryCode || 'countryCode'"
            [showErrors]="true"
            [formControlInvalid]="
              !!(
                formGroup.get(inputFieldConfig.name)?.invalid && formGroup.get(inputFieldConfig.name)?.touched
              )
            "
            [validationConfig]="validationConfig"
            [helperText]="renderedHelperTextContent"
            [isVerifyButtonShown]="inputFieldConfig?.showPhoneVerifyButton ?? false"
            [isVerifyButtonGlobalStylingAllowed]="isVerifyButtonGlobalStylingAllowed"
            [verifyButtonGlobalStylingClass]="verifyButtonGlobalStylingClass"
            [verifyButtonStyle]="verifyButtonStyle"
            [verifyButtonTextContent]="verifyButtonTextContent"
            [spaceBetweenElementUnit]="inputFieldConfig?.spaceBetweenElementUnit ?? ''"
            [spaceBetweenElementValue]="inputFieldConfig?.spaceBetweenElementValue ?? 0"
            [attachToInput]="inputFieldConfig?.phoneNumberConfig?.attachToInput ?? false"
            [showCountryFlag]="inputFieldConfig?.phoneNumberConfig?.showCountryFlag ?? false"
            [allowSearch]="inputFieldConfig?.phoneNumberConfig?.allowSearch ?? false"
            [showDropdownMenuCountryFlag]="
              inputFieldConfig?.phoneNumberConfig?.showDropdownMenuCountryFlag ?? false
            "
            [showCountryName]="inputFieldConfig?.phoneNumberConfig?.showCountryName ?? false"
            [inputFormat]="inputFieldConfig.inputFormat"
            [navigationOptions]="widgetNavigationOptions"
            [formGroup]="formGroup"
            [disableAutocomplete]="disableAutocomplete"
            [parentFormId]="inputFieldConfig?.parentForm ?? ''"
            [nestedFormId]="nestedFormId"
            [allowedCountryCodes]="inputFieldConfig?.phoneNumberConfig?.allowedCountryCodes ?? []"
            [inputLabelTooltipText]="renderedLabelTooltipTextContent"
          >
          </finxone-phone-number>
        }

        <!-- Text Area -->
        @case (InputMode.TEXT_AREA) {
          <finxone-textarea
            [formControlName]="inputFieldConfig.name"
            [id]="inputFieldConfig.name"
            [parentControl]="parentControl"
            [label]="inputFieldConfig?.label ?? ''"
            [placeholder]="inputFieldConfig?.inputPlaceholder ?? ''"
            [showErrors]="true"
            [value]="formGroup.controls[inputFieldConfig.name]?.value ?? ''"
            [formControlInvalid]="
              !!(
                formGroup.get(inputFieldConfig.name)?.invalid && formGroup.get(inputFieldConfig.name)?.touched
              )
            "
            [validationConfig]="validationConfig"
            [infoText]="renderedHelperTextContent"
            [disableAutocomplete]="disableAutocomplete"
            [inputLabelTooltipText]="renderedLabelTooltipTextContent"
          ></finxone-textarea>
        }

        <!-- Currency Amount Input -->
        @case (InputMode.CURRENCY_AMOUNT) {
          <finxone-currency-amount-input
            [inputFieldConfig]="inputFieldConfig"
            [formControlName]="inputFieldConfig.name"
            [id]="inputFieldConfig.name"
            [parentControl]="parentControl"
            [showErrors]="true"
            [value]="formGroup.controls[inputFieldConfig.name]?.value ?? ''"
            [formControlInvalid]="
              !!(
                formGroup.get(inputFieldConfig.name)?.invalid && formGroup.get(inputFieldConfig.name)?.touched
              )
            "
            [validationConfig]="validationConfig"
            [textStyleClass]="currencyAmountFieldTextStyleClass"
            [infoText]="renderedHelperTextContent"
            [accountTypeContext]="inputFieldConfig?.accountType ?? defaultAccountType"
            [disableAutocomplete]="disableAutocomplete"
            [inputLabelTooltipText]="renderedLabelTooltipTextContent"
          ></finxone-currency-amount-input>
        }

        <!-- Checkbox -->
        @case (InputMode.CHECKBOX) {
          <finxone-checkbox
            [label]="inputFieldConfig?.label ?? ''"
            [name]="inputFieldConfig.name"
            [id]="inputFieldConfig.name"
            [options]="options"
            [formControlName]="inputFieldConfig.name"
            [parentControl]="parentControl"
            [optionLabelKey]="'label'"
            [optionValueKey]="'value'"
            [formControlInvalid]="
              !!(
                formGroup.get(inputFieldConfig.name)?.invalid && formGroup.get(inputFieldConfig.name)?.touched
              )
            "
            [validationConfig]="validationConfig"
            [helperText]="renderedHelperTextContent"
            [value]="formGroup.controls[inputFieldConfig.name]?.value ?? []"
            [radioCheckboxOptionsLayout]="inputFieldConfig?.radioCheckboxOptionsLayout || 'vertical'"
            [inputLabelTooltipText]="renderedLabelTooltipTextContent"
          ></finxone-checkbox>
        }

        <!-- Radio -->
        @case (InputMode.RADIO) {
          <finxone-radio
            [label]="inputFieldConfig?.label ?? ''"
            [name]="inputFieldConfig.name"
            [id]="inputFieldConfig.name"
            [options]="options"
            [formControlName]="inputFieldConfig.name"
            [parentControl]="parentControl"
            [optionLabelKey]="'label'"
            [optionValueKey]="'value'"
            [formControlInvalid]="
              !!(
                formGroup.get(inputFieldConfig.name)?.invalid && formGroup.get(inputFieldConfig.name)?.touched
              )
            "
            [validationConfig]="validationConfig"
            [helperText]="renderedHelperTextContent"
            [value]="formGroup.controls[inputFieldConfig.name]?.value ?? ''"
            [radioCheckboxOptionsLayout]="inputFieldConfig?.radioCheckboxOptionsLayout || 'vertical'"
            [inputLabelTooltipText]="renderedLabelTooltipTextContent"
          >
          </finxone-radio>
        }

        <!-- Date picker -->
        @case (InputMode.DATE_PICKER) {
          <finxone-datepicker
            [formControlName]="inputFieldConfig.name"
            [dayFormControl]="getFormControlByName(inputFieldConfig.day || 'day')"
            [monthFormControl]="getFormControlByName(inputFieldConfig.month || 'month')"
            [yearFormControl]="getFormControlByName(inputFieldConfig.year || 'year')"
            [minDate]="inputFieldConfig.minValue ?? ''"
            [maxDate]="inputFieldConfig.maxValue ?? ''"
            [name]="inputFieldConfig.name"
            [id]="inputFieldConfig?.id ?? ''"
            [label]="inputFieldConfig?.label ?? ''"
            [helperText]="renderedHelperTextContent"
            [dateFormat]="inputFieldConfig?.dateFormat"
            [placeholder]="inputFieldConfig?.inputPlaceholder ?? ''"
            [spaceBetweenElementUnit]="inputFieldConfig?.spaceBetweenElementUnit ?? ''"
            [spaceBetweenElementValue]="inputFieldConfig?.spaceBetweenElementValue ?? 0"
            [isScrollable]="inputFieldConfig?.isScrollable ?? true"
            [isScrollPeekEnabled]="inputFieldConfig?.isScrollPeekEnabled ?? false"
            [visibleItemsCount]="inputFieldConfig?.visibleItemsCount ?? defaultVisibleItemsCount"
            [menuDirection]="inputFieldConfig?.menuDirection ?? defaultMenuDirection"
            [showErrors]="true"
            [formControlInvalid]="
              !!(
                formGroup.get(inputFieldConfig.name)?.invalid && formGroup.get(inputFieldConfig.name)?.touched
              )
            "
            [validationConfig]="validationConfig"
            [inputLabelTooltipText]="renderedLabelTooltipTextContent"
          >
          </finxone-datepicker>
        }

        <!-- Dropdown input field -->
        @case (InputMode.DROPDOWN) {
          <app-dropdown-input-field
            [formGroup]="formGroup"
            [inputFieldConfig]="inputFieldConfig"
            [validationConfig]="validationConfig"
            [formControlInvalid]="
              !!(
                formGroup.get(inputFieldConfig.name)?.invalid && formGroup.get(inputFieldConfig.name)?.touched
              )
            "
            [renderedHelperTextContent]="renderedHelperTextContent ?? ''"
            [inputLabelTooltipText]="renderedLabelTooltipTextContent"
          >
          </app-dropdown-input-field>
        }

        <!-- Slider -->
        @case (InputMode.SLIDER) {
          <finxone-platform-slider
            [formControlName]="inputFieldConfig.name"
            [min]="renderedSliderMinValue ?? 0"
            [max]="renderedSliderMaxValue ?? 100"
            [step]="inputFieldConfig?.stepValue ?? 1"
            [label]="inputFieldConfig?.label ?? ''"
          ></finxone-platform-slider>
        }

        <!-- Autocomplete -->
        @case (InputMode.AUTOCOMPLETE) {
          <finxone-autocomplete-input
            [label]="inputFieldConfig?.label ?? ''"
            [id]="inputFieldConfig.name"
            [filteredOptions]="options"
            [formControlName]="inputFieldConfig.name"
            [parentControl]="parentControl"
            [optionLabelKey]="'label'"
            [optionValueKey]="'value'"
            [formControlInvalid]="
              !!(
                formGroup.get(inputFieldConfig.name)?.invalid && formGroup.get(inputFieldConfig.name)?.touched
              )
            "
            [validationConfig]="validationConfig"
            [helperText]="renderedHelperTextContent"
            [inputLabelTooltipText]="renderedLabelTooltipTextContent"
            [selectedOption]="formGroup.controls[inputFieldConfig.name]?.value ?? ''"
            [formGroup]="formGroup"
          ></finxone-autocomplete-input>
        }

        <!-- Switch -->
        @case (InputMode.SWITCH) {
          <finxone-switch
            [id]="inputFieldConfig.name"
            [name]="inputFieldConfig.name"
            [formControlName]="inputFieldConfig.name"
            [parentControl]="parentControl"
            [label]="inputFieldConfig?.label ?? ''"
            [showErrors]="true"
            [formControlInvalid]="
              !!(
                formGroup.get(inputFieldConfig.name)?.invalid && formGroup.get(inputFieldConfig.name)?.touched
              )
            "
            [validationConfig]="validationConfig"
            [infoText]="renderedHelperTextContent"
            [inputLabelTooltipText]="renderedLabelTooltipTextContent"
            [isSmallSwitchButton]="inputFieldConfig.inputStyle !== 'large'"
          ></finxone-switch>
        }
        @default {
          <ng-container
            [ngTemplateOutlet]="inputTextTemplate"
            [ngTemplateOutletContext]="{
              config: inputFieldConfig,
              group: formGroup,
            }"
          >
          </ng-container>
        }
      }
    </div>

    <ng-template
      #inputTextTemplate
      let-config="config"
      let-group="group"
      let-isVerifyButtonShown="isVerifyButtonShown"
    >
      <finxone-input
        [id]="config.name"
        [formControlName]="config.name"
        [parentControl]="parentControl"
        [label]="config?.label ?? ''"
        [type]="config?.type ?? 'text'"
        [placeholder]="config?.inputPlaceholder ?? ''"
        [value]="group.controls[config.name]?.value ?? ''"
        [showErrors]="true"
        [formControlInvalid]="!!(group.get(config.name)?.invalid && group.get(config.name)?.touched)"
        [validationConfig]="validationConfig"
        [infoText]="renderedHelperTextContent"
        [isVerifyButtonShown]="isVerifyButtonShown ?? false"
        [isVerifyButtonGlobalStylingAllowed]="isVerifyButtonGlobalStylingAllowed"
        [verifyButtonGlobalStylingClass]="verifyButtonGlobalStylingClass"
        [verifyButtonStyle]="verifyButtonStyle"
        [verifyButtonTextContent]="verifyButtonTextContent"
        [spaceBetweenElementUnit]="config?.spaceBetweenElementUnit ?? ''"
        [spaceBetweenElementValue]="config?.spaceBetweenElementValue ?? 0"
        [disableAutocomplete]="disableAutocomplete"
        [inputLabelTooltipText]="renderedLabelTooltipTextContent"
      ></finxone-input>
    </ng-template>
  </div>
}
